import React, { useEffect, useState } from "react";
import "../assets/js/main.min.js";
import { useNavigate } from "react-router-dom";
import "../assets/css/main.min.css";
import "../assets/css/custom.css";
import "react-datepicker/dist/react-datepicker.css";
import { useDispatch, useSelector } from "react-redux";
import { getProfile } from "../store/admin/action.js";
import male from "../assets/images/male.png";
import { getUser, removeHost } from "../store/user/action.js";
import { formatCoin } from "../util/formatCoin";
import { formatDollar } from "../util/formatDollar";
import leftArrow from "../assets/images/leftArrow.png";
import { useTranslation } from "react-i18next";

const T = {
  pageBg: "#33083e",
  text: "#F5F3FF",
  sub: "#B8A9C9",
  border: "rgba(255,255,255,0.12)",
  card: "#3c134e",
  cardSoft: "#45185a",
  radius: 16,
  shadow: "0 12px 28px rgba(0,0,0,0.28)",
  pink: "#E8538F",
  pinkGrad: "linear-gradient(180deg, #F861B1 0%, #E91384 100%)",
};

const ACCENTS = {
  blue: { bg: "rgba(59,130,246,0.16)", fg: "#60A5FA" },
  green: { bg: "rgba(34,197,94,0.16)", fg: "#34D399" },
  amber: { bg: "rgba(245,158,11,0.16)", fg: "#FBBF24" },
  pink: { bg: "rgba(236,72,153,0.16)", fg: "#F472B6" },
  indigo: { bg: "rgba(99,102,241,0.16)", fg: "#A5B4FC" },
  violet: { bg: "rgba(168,85,247,0.16)", fg: "#C084FC" },
};

const Admin = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const admin = useSelector((state) => state.admin.seller);
  const { user } = useSelector((state) => state.user);
  const agencyId = localStorage.getItem("agencyId");
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 720);
  const [pendingRemove, setPendingRemove] = useState(null);
  const [removing, setRemoving] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 720);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    dispatch(getProfile(agencyId));
    dispatch(getUser(agencyId, { start: 1, limit: 20 }));

    // This dashboard's stats (Coin Target progress, salary, level) previously only ever loaded
    // once on mount — if the agency owner kept the tab open, the numbers stayed frozen at
    // whatever they were on first load even as real gift activity kept updating them server-side.
    // Poll on an interval, and refetch immediately whenever the tab regains focus/visibility, so
    // the numbers shown here can never silently go stale for more than a short window.
    const REFRESH_MS = 30 * 1000;
    const intervalId = setInterval(() => {
      if (document.visibilityState === "visible") {
        dispatch(getProfile(agencyId));
      }
    }, REFRESH_MS);

    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        dispatch(getProfile(agencyId));
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [dispatch, agencyId]);

  const handleOpenHostHistory = (id) =>
    navigate("/agencypanel/hosthistory", { state: id });
  const handleRemoveHost = (u) => {
    if (!u?._id || !agencyId || removing) return;
    // In-app confirm — Android WebView often blocks window.confirm
    setPendingRemove(u);
  };
  const confirmRemoveHost = async () => {
    if (!pendingRemove?._id || !agencyId || removing) return;
    setRemoving(true);
    try {
      await dispatch(removeHost(pendingRemove._id, agencyId));
    } finally {
      setRemoving(false);
      setPendingRemove(null);
    }
  };

  const fmt = (v) => formatCoin(v);
  const createdAt = admin?.createdAt ? new Date(admin.createdAt) : null;

  const week = admin?.weekly?.agencyThisWeek;
  // STRICT: missing week → 0. Never lifetime currentHostCoin / rCoin / totalCoin.
  const weekCoins = Number(week?.eligibleCoins ?? 0) || 0;
  const weekSalary = Number(week?.salaryUsd ?? 0) || 0;
  const weekLevel = Number(week?.level ?? 0) || 0;
  const withdrawableUsd =
    admin?.withdrawableUsd != null
      ? Number(admin.withdrawableUsd)
      : Number(admin?.appSalaryDollar) || 0;
  const progressPct = 0; // level-relative % optional; show 0 until next-tier wire exists

  const stats = [
    {
      label: "Main Withdrawable USD",
      value: formatDollar(withdrawableUsd),
      dollar: "Spendable balance",
      accent: "green",
      icon: <IconWallet />,
    },
    {
      label: "Agency Salary This Week",
      value: formatDollar(weekSalary),
      dollar: admin?.weekId ? `Week ${admin.weekId}` : "Current week",
      accent: "blue",
      icon: <IconWallet />,
    },
    {
      label: "Weekly Target Progress",
      value: formatCoin(weekCoins),
      dollar: `${progressPct}%`,
      accent: "amber",
      icon: <IconCoins />,
    },
    {
      label: "Agency Level",
      value: weekLevel,
      dollar: "This week",
      accent: "indigo",
      icon: <IconCoinStack />,
    },
    {
      label: t("total_hosts"),
      value: admin?.totalAgencyWiseHost || 0,
      accent: "pink",
      icon: <IconUsers />,
      onMore: () => navigate("/agencypanel/creators"),
    },
  ];

  const shortcuts = [
    {
      title: t("total_hosts"),
      value: String(user?.length ?? admin?.totalAgencyWiseHost ?? 0),
      onMore: () => navigate("/agencypanel/creators"),
      icon: <HostGlyph />,
    },
    {
      title: "Weekly Target",
      value: formatCoin(weekCoins),
      icon: <MoneyGlyph />,
    },
    {
      title: "Salary This Week",
      value: `${formatDollar(weekSalary)} · L${weekLevel}`,
      icon: <CoinGlyph />,
    },
  ];

  return (
    <>
      <div
        style={{
          background: T.pageBg,
          minHeight: "100%",
          color: T.text,
          width: "100%",
          maxWidth: "100%",
          overflowX: "hidden",
        }}
      >
        <div
          style={{
            position: "sticky",
            top: 0,
            zIndex: 9,
            background: T.pageBg,
            padding: "14px 16px",
            borderBottom: `1px solid ${T.border}`,
          }}
        >
          <div style={{ fontWeight: 700, fontSize: 18, color: T.text }}>
            {t("dashboard")}
          </div>
          <div style={{ fontSize: 12, color: T.sub, marginTop: 2 }}>
            {t("welcome_dashboard")}
          </div>
        </div>

        <div style={{ padding: "12px 16px 24px", width: "100%" }}>
          {/* Profile */}
          <div
            style={{
              background: T.card,
              border: `1px solid ${T.border}`,
              borderRadius: T.radius,
              boxShadow: T.shadow,
              padding: 16,
              marginBottom: 14,
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                textAlign: "center",
                padding: "8px 4px 4px",
              }}
            >
              <div
                style={{
                  width: 76,
                  height: 76,
                  borderRadius: "50%",
                  background: "linear-gradient(90deg, #E72392 0%, #9029FF 100%)",
                  padding: 2,
                  marginBottom: 10,
                }}
              >
                <div
                  style={{
                    width: "100%",
                    height: "100%",
                    borderRadius: "50%",
                    background: `url(${admin?.image || male}) center/cover no-repeat`,
                  }}
                />
              </div>
              <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 6 }}>
                {admin?.name || "Agency"}
              </div>
              {admin?.agencyCode && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    color: T.sub,
                    fontSize: 13,
                    fontWeight: 600,
                  }}
                >
                  AG-{admin.agencyCode}
                </div>
              )}
            </div>

            {/* 2x2 / 2x3 modern app cards */}
            <div className="agency-stat-grid" style={{ marginTop: 16 }}>
              {stats.map((s) => (
                <StatCard key={s.label} {...s} />
              ))}
            </div>

            <div
              style={{
                marginTop: 14,
                color: T.sub,
                fontSize: 13,
                lineHeight: 1.5,
              }}
            >
              {admin?.about ||
                "We help creators grow with data-driven insights and creative direction."}
            </div>

            <div
              style={{
                marginTop: 12,
                display: "flex",
                gap: 10,
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
              }}
            >
              <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                <span
                  style={{
                    fontWeight: 600,
                    fontSize: 12,
                    color: "#fff",
                    background:
                      admin?.isActive === false ? "#FA1E43" : "#0AAD0F",
                    padding: "4px 10px",
                    borderRadius: 999,
                  }}
                >
                  {t("status")}:{" "}
                  {admin?.isActive === false ? "Inactive" : "Active"}
                </span>
                <span style={{ fontWeight: 600, fontSize: 13, color: T.sub }}>
                  {t("created")}:{" "}
                  {createdAt ? createdAt.toLocaleDateString("en-GB") : "-"}
                </span>
              </div>
            </div>
          </div>

          {/* Shortcut cards 2-col */}
          <div className="agency-stat-grid" style={{ marginBottom: 8 }}>
            {shortcuts.map((s) => (
              <ShortcutCard key={s.title} {...s} />
            ))}
          </div>

          {/* Host list */}
          <div style={{ marginTop: 8 }}>
            <SectionHeader title="Host Details" />
            {(user || []).slice(0, 2).map((u) => (
              <HostItem
                key={u?._id}
                u={u}
                onRemove={() => handleRemoveHost(u)}
                onHistory={() => handleOpenHostHistory(u?._id)}
              />
            ))}
            {(user || []).length > 2 && (
              <div
                style={{
                  marginTop: 8,
                  textAlign: "center",
                  cursor: "pointer",
                  color: "#E2CAD9",
                  fontWeight: 600,
                  fontSize: 14,
                  textDecoration: "underline",
                }}
                onClick={() => navigate("/agencypanel/creators")}
              >
                {t("see_more")}
              </div>
            )}
          </div>
        </div>
      </div>

      {pendingRemove && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.55)",
            zIndex: 10050,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 20,
          }}
          onClick={() => !removing && setPendingRemove(null)}
        >
          <div
            style={{
              background: T.card,
              border: `1px solid ${T.border}`,
              borderRadius: 16,
              padding: 20,
              width: "100%",
              maxWidth: 340,
              color: T.text,
              boxShadow: T.shadow,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 8 }}>
              {t("remove_host")}
            </div>
            <div style={{ color: T.sub, fontSize: 14, lineHeight: 1.45, marginBottom: 18 }}>
              {t("remove_host_confirm", {
                defaultValue: `Remove ${pendingRemove?.name || "this host"} from your agency?`,
                name: pendingRemove?.name || "this host",
              })}
            </div>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button
                type="button"
                disabled={removing}
                onClick={() => setPendingRemove(null)}
                style={{
                  border: `1px solid ${T.border}`,
                  background: "transparent",
                  color: T.text,
                  borderRadius: 10,
                  padding: "10px 14px",
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                {t("cancel", { defaultValue: "Cancel" })}
              </button>
              <button
                type="button"
                disabled={removing}
                onClick={confirmRemoveHost}
                style={{
                  border: "none",
                  background: "#F43F5E",
                  color: "#fff",
                  borderRadius: 10,
                  padding: "10px 14px",
                  fontWeight: 700,
                  cursor: removing ? "wait" : "pointer",
                  opacity: removing ? 0.7 : 1,
                }}
              >
                {removing ? "..." : t("remove_host")}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const StatCard = ({ label, value, dollar, hint, accent = "pink", onMore, icon }) => {
  const a = ACCENTS[accent] || ACCENTS.pink;
  return (
    <button
      type="button"
      onClick={onMore}
      style={{
        border: `1px solid ${T.border}`,
        background: T.cardSoft,
        borderRadius: 14,
        padding: "14px 12px",
        textAlign: "left",
        cursor: onMore ? "pointer" : "default",
        boxShadow: T.shadow,
        minWidth: 0,
        width: "100%",
        color: T.text,
        position: "relative",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: 4,
          height: "100%",
          background: a.fg,
          borderRadius: "14px 0 0 14px",
        }}
      />
      <div
        style={{
          width: 36,
          height: 36,
          borderRadius: 11,
          background: a.bg,
          color: a.fg,
          display: "grid",
          placeItems: "center",
          marginBottom: 10,
        }}
      >
        {icon}
      </div>
      <div
        style={{
          fontSize: 12,
          fontWeight: 600,
          color: T.sub,
          lineHeight: 1.3,
          marginBottom: 8,
          wordBreak: "break-word",
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 22,
          fontWeight: 700,
          color: T.text,
          letterSpacing: "-0.02em",
          wordBreak: "break-word",
        }}
      >
        {value}
      </div>
      {dollar ? (
        <div
          style={{
            marginTop: 6,
            fontSize: 15,
            fontWeight: 700,
            color: "#34D399",
          }}
        >
          {dollar}
        </div>
      ) : null}
      {hint ? (
        <div
          style={{
            marginTop: 4,
            fontSize: 11,
            fontWeight: 600,
            color: T.sub,
          }}
        >
          {hint}
        </div>
      ) : null}
    </button>
  );
};

const ShortcutCard = ({ icon, title, value, onMore }) => (
  <button
    type="button"
    onClick={onMore}
    style={{
      border: `1px solid ${T.border}`,
      background: T.card,
      borderRadius: 14,
      padding: 12,
      display: "flex",
      alignItems: "center",
      gap: 10,
      cursor: "pointer",
      boxShadow: T.shadow,
      color: T.text,
      width: "100%",
      minWidth: 0,
      textAlign: "left",
    }}
  >
    <div
      style={{
        width: 40,
        height: 40,
        borderRadius: 10,
        background: "rgba(255,255,255,0.06)",
        display: "grid",
        placeItems: "center",
        flexShrink: 0,
        color: T.pink,
      }}
    >
      {icon}
    </div>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div
        style={{
          fontSize: 12,
          color: T.sub,
          fontWeight: 600,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {title}
      </div>
      <div style={{ fontSize: 18, fontWeight: 700, color: "#85F535", marginTop: 2 }}>
        {value}
      </div>
    </div>
  </button>
);

const SectionHeader = ({ title }) => (
  <div
    style={{
      padding: "10px 0",
      marginBottom: 4,
      color: T.text,
      fontWeight: 600,
      fontSize: 16,
    }}
  >
    {title}
  </div>
);

const HostItem = ({ u, onRemove, onHistory }) => {
  const { t } = useTranslation();
  return (
    <div
      style={{
        background: T.card,
        border: `1px solid ${T.border}`,
        borderRadius: 12,
        padding: 12,
        marginBottom: 10,
        boxShadow: T.shadow,
        color: T.text,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <img
          src={u?.image || male}
          alt=""
          style={{
            width: 42,
            height: 42,
            borderRadius: "50%",
            objectFit: "cover",
            border: `1px solid ${T.border}`,
            flexShrink: 0,
          }}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div
              style={{
                fontWeight: 600,
                fontSize: 14,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {u?.name || "-"}
            </div>
            <span
              style={{
                background: "#7B11E3",
                color: "#fff",
                fontSize: 11,
                borderRadius: 4,
                padding: "2px 5px",
                flexShrink: 0,
              }}
            >
              {t("host")}
            </span>
          </div>
          <div style={{ color: T.sub, fontWeight: 600, fontSize: 13, marginTop: 2 }}>
            {t("ID")}: {u?.uniqueId || "-"}
          </div>
        </div>

        <button
          type="button"
          onClick={onRemove}
          style={{
            border: "none",
            background: "rgba(244,63,94,0.16)",
            color: "#F43F5E",
            borderRadius: 10,
            padding: "8px 12px",
            fontWeight: 700,
            fontSize: 12,
            cursor: "pointer",
            flexShrink: 0,
          }}
        >
          {t("remove_host")}
        </button>
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginTop: 12,
          gap: 8,
          flexWrap: "wrap",
        }}
      >
        <div
          style={{
            background: "#f2205f",
            color: "#fff",
            padding: "4px 8px",
            borderRadius: 8,
            fontWeight: 600,
            fontSize: 13,
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          {t("host_income")}:
          {formatCoin(u?.weekCoins != null ? u.weekCoins : u?.currentCoin || 0)}
          <span style={{ opacity: 0.85, fontWeight: 500, fontSize: 11 }}>
            this week
          </span>
        </div>

        <button
          type="button"
          onClick={onHistory}
          style={{
            border: "none",
            background: "transparent",
            color: "#E2CAD9",
            fontWeight: 600,
            fontSize: 13,
            display: "flex",
            alignItems: "center",
            gap: 6,
            cursor: "pointer",
          }}
        >
          {t("history")}
          <img src={leftArrow} alt="" height={18} width={18} />
        </button>
      </div>
    </div>
  );
};

const HostGlyph = () => (
  <svg
    stroke="currentColor"
    fill="none"
    strokeWidth="2"
    viewBox="0 0 24 24"
    strokeLinecap="round"
    strokeLinejoin="round"
    height="1.4em"
    width="1.4em"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
    <circle cx="9" cy="7" r="4"></circle>
    <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
    <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
  </svg>
);

const MoneyGlyph = () => (
  <svg
    stroke="currentColor"
    fill="none"
    strokeWidth="2"
    viewBox="0 0 24 24"
    strokeLinecap="round"
    strokeLinejoin="round"
    height="1.4em"
    width="1.4em"
    xmlns="http://www.w3.org/2000/svg"
  >
    <line x1="12" y1="1" x2="12" y2="23"></line>
    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
  </svg>
);

const CoinGlyph = () => (
  <svg
    stroke="currentColor"
    fill="none"
    strokeWidth="2"
    viewBox="0 0 24 24"
    strokeLinecap="round"
    strokeLinejoin="round"
    height="1.4em"
    width="1.4em"
    xmlns="http://www.w3.org/2000/svg"
  >
    <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"></polyline>
    <polyline points="17 6 23 6 23 12"></polyline>
  </svg>
);

const iconProps = {
  width: 18,
  height: 18,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round",
  strokeLinejoin: "round",
};

const IconPercent = () => (
  <svg {...iconProps}>
    <line x1="19" y1="5" x2="5" y2="19" />
    <circle cx="6.5" cy="6.5" r="2.5" />
    <circle cx="17.5" cy="17.5" r="2.5" />
  </svg>
);
const IconWallet = () => (
  <svg {...iconProps}>
    <path d="M20 7H4a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2z" />
    <path d="M16 12h.01" />
  </svg>
);
const IconCoins = () => (
  <svg {...iconProps}>
    <circle cx="8" cy="8" r="6" />
    <path d="M18.09 10.37A6 6 0 1 1 10.34 18" />
    <path d="M7 6h1v4" />
  </svg>
);
const IconUsers = () => (
  <svg {...iconProps}>
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);
const IconCoinStack = () => (
  <svg {...iconProps}>
    <ellipse cx="12" cy="5" rx="8" ry="3" />
    <path d="M4 5v6c0 1.7 3.6 3 8 3s8-1.3 8-3V5" />
    <path d="M4 11v6c0 1.7 3.6 3 8 3s8-1.3 8-3v-6" />
  </svg>
);
const IconChart = () => (
  <svg {...iconProps}>
    <path d="M3 3v18h18" />
    <path d="M7 14l4-4 4 3 5-6" />
  </svg>
);

export default Admin;
